/**
 * ============================================================================
 * AUTH SERVICE - API CLIENT
 * ============================================================================
 * 
 * Handles all authentication-related API calls
 */

import api, { handleApiError } from './api';

/**
 * Sign up with phone number
 */
export const signupWithPhone = async (data) => {
  try {
    const response = await api.post('/auth/signup/phone', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Verify signup OTP
 */
export const verifySignupOTP = async (data) => {
  try {
    const response = await api.post('/auth/signup/verify', data);
    
    // Store tokens
    if (response.data.data?.tokens) {
      localStorage.setItem('accessToken', response.data.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.data.tokens.refreshToken);
    }
    
    // Store user
    if (response.data.data?.user) {
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Login with phone number
 */
export const loginWithPhone = async (data) => {
  try {
    const response = await api.post('/auth/login', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Verify login OTP
 */
export const verifyLoginOTP = async (data) => {
  try {
    const response = await api.post('/auth/login/verify', data);
    
    // Store tokens
    if (response.data.data?.tokens) {
      localStorage.setItem('accessToken', response.data.data.tokens.accessToken);
      localStorage.setItem('refreshToken', response.data.data.tokens.refreshToken);
    }
    
    // Store user
    if (response.data.data?.user) {
      localStorage.setItem('user', JSON.stringify(response.data.data.user));
    }
    
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Refresh access token
 */
export const refreshToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await api.post('/auth/refresh', { refreshToken });
    
    if (response.data.data?.tokens) {
      localStorage.setItem('accessToken', response.data.data.tokens.accessToken);
      if (response.data.data.tokens.refreshToken) {
        localStorage.setItem('refreshToken', response.data.data.tokens.refreshToken);
      }
    }
    
    return response.data;
  } catch (error) {
    // Clear tokens on refresh failure
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    throw handleApiError(error);
  }
};

/**
 * Logout
 */
export const logout = async () => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    // Continue with logout even if API call fails
    console.error('Logout API error:', error);
  } finally {
    // Clear local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }
};

/**
 * Request password reset
 */
export const requestPasswordReset = async (data) => {
  try {
    const response = await api.post('/auth/password/reset/request', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Reset password (deprecated - use confirmPasswordReset instead)
 */
export const resetPassword = async (data) => {
  try {
    const response = await api.post('/auth/password/reset/confirm', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Reset password (confirm password reset)
 */
export const confirmPasswordReset = async (data) => {
  try {
    const response = await api.post('/auth/password/reset/confirm', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Change password (not implemented in backend yet)
 * Note: Backend doesn't have /auth/password/change endpoint
 * Users should use password reset flow instead
 */
export const changePassword = async (data) => {
  try {
    // This endpoint doesn't exist yet - would need to be implemented
    // For now, users should use password reset flow
    throw new Error('Password change not yet implemented. Please use password reset.');
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Resend OTP
 */
export const resendOTP = async (data) => {
  try {
    const response = await api.post('/auth/otp/resend', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get current user
 */
export const getCurrentUser = async () => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Add email to account
 */
export const addEmail = async (data) => {
  try {
    const response = await api.post('/auth/email/add', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Verify email
 */
export const verifyEmail = async (data) => {
  try {
    const response = await api.post('/auth/email/verify', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Set password
 */
export const setPassword = async (data) => {
  try {
    const response = await api.post('/auth/password/set', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default {
  signupWithPhone,
  verifySignupOTP,
  loginWithPhone,
  verifyLoginOTP,
  refreshToken,
  logout,
  requestPasswordReset,
  confirmPasswordReset,
  resetPassword,
  changePassword,
  resendOTP,
  getCurrentUser,
  addEmail,
  verifyEmail,
  setPassword,
};
